import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Irrigation Zone Schedule (2026-10-05) — read-only, customer-facing.
// Data comes from ERP via GET /api/customer/{id}/zone-schedule, which only
// answers enabled=true when the property belongs to this customer, is
// active, and has an Irrigation System with "Show Zone Schedule in Portal" ON.
// This page never shows Rachio data and is independent of the Water
// Conservation page. GPM / gallons and the Watering Days box (2026-10-18) are
// informational only: they never change a controller, Rachio or watering.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PAGE_BG = 'radial-gradient(circle at top, #1186c5 0%, #0b3b57 35%, #082c40 100%)';

// "12 min"; blank when the zone has no run time.
const fmtTime = (z) => (z.run_time_minutes || z.run_time_minutes === 0 ? `${z.run_time_minutes} min` : '');
// GPM / gallons: blank when unknown — never shown as 0.
const fmtGpm = (z) => (z.gpm ? `${z.gpm} GPM` : '');
const fmtGal = (g) => (g === null || g === undefined ? '' : `${g >= 100 ? Math.round(g).toLocaleString() : g} gal`);

function WateringDaysBox({ wd }) {
  if (!wd) return null;
  return (
    <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="watering-days">
      <h2 className="text-sm font-black uppercase italic tracking-wide text-cyan-100">Watering Days</h2>
      {wd.verified ? (
        <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm text-slate-100 sm:grid-cols-2">
          <div><dt className="text-xs uppercase tracking-wider text-slate-400">City</dt>
            <dd className="font-semibold" data-testid="wd-city">{wd.city}</dd></div>
          <div><dt className="text-xs uppercase tracking-wider text-slate-400">Watering days</dt>
            <dd className="font-semibold text-white" data-testid="wd-days">
              {wd.days_display}
              {wd.mode === 'manual' && (
                <span className="ml-2 rounded-full bg-orange-500/20 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-orange-200" data-testid="wd-manual">
                  {wd.label || 'Manually Set'}
                </span>
              )}
            </dd></div>
          <div><dt className="text-xs uppercase tracking-wider text-slate-400">Season</dt>
            <dd data-testid="wd-season">{wd.season?.name} ({wd.season?.dates}){wd.season?.frequency ? ` · ${wd.season.frequency}` : ''}</dd></div>
          <div><dt className="text-xs uppercase tracking-wider text-slate-400">Source</dt>
            <dd data-testid="wd-source">
              {wd.source?.url
                ? <a href={wd.source.url} target="_blank" rel="noreferrer" className="text-cyan-200 underline">{wd.source.name}</a>
                : wd.source?.name}
              {wd.source?.detail && <span className="block text-xs text-slate-400">{wd.source.detail}</span>}
            </dd></div>
          {wd.rule && <p className="text-xs text-slate-300 sm:col-span-2" data-testid="wd-rule">{wd.rule}</p>}
          {wd.note && <p className="text-xs text-amber-200 sm:col-span-2" data-testid="wd-note">{wd.note}</p>}
        </dl>
      ) : (
        <>
          <p className="mt-2 text-sm text-slate-200" data-testid="wd-not-verified">
            {wd.message || 'Watering days not verified'}{wd.city ? ` · ${wd.city}` : ''}
          </p>
          {wd.note && <p className="mt-1 text-xs text-slate-400" data-testid="wd-note">{wd.note}</p>}
        </>
      )}
      <p className="mt-3 text-[11px] text-slate-400">For information only — this does not change your controller.</p>
    </section>
  );
}

export default function ZoneSchedule() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const customer = JSON.parse(localStorage.getItem('customer') || '{}');
  const serviceLocation = new URLSearchParams(window.location.search).get('service_location') || '';

  useEffect(() => {
    if (!customer.id) { navigate('/'); return undefined; }
    // Clear the previous property's schedule immediately so a property
    // switch never shows (or retains) another property's zones.
    setData(null);
    let cancelled = false;
    const qs = serviceLocation ? `?service_location=${encodeURIComponent(serviceLocation)}` : '';
    fetch(`${BACKEND_URL}/api/customer/${encodeURIComponent(customer.id)}/zone-schedule${qs}`)
      .then(r => (r.ok ? r.json() : { enabled: false, systems: [] }))
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ enabled: false, systems: [] }); });
    return () => { cancelled = true; };
  }, [customer.id, serviceLocation, navigate]);

  const multi = (data?.systems || []).length > 1;

  return (
    <div style={{ minHeight: '100vh', background: PAGE_BG }}>
      <div className="mx-auto max-w-5xl px-4 py-6 md:px-6 md:py-10">
        <button
          onClick={() => navigate('/dashboard')}
          className="mb-6 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-slate-100 hover:bg-white/10"
        >
          ← Back to Dashboard
        </button>

        <h1 className="text-2xl font-black uppercase italic tracking-tight text-white md:text-3xl">Zone Schedule</h1>
        {data?.enabled && data.location_name && (
          <p className="mt-1 text-sm text-cyan-100/80" data-testid="zone-location">{data.location_name}</p>
        )}

        {data === null && <div className="mt-8 text-sm text-slate-300">Loading…</div>}

        {data?.enabled && <WateringDaysBox wd={data.watering_days} />}

        {data && !data.enabled && (
          <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-slate-200" data-testid="zone-unavailable">
            A zone schedule isn't available for this property yet. Please contact us at{' '}
            <a href="tel:4697513567" className="text-cyan-200 underline">469-751-3567</a> if you have questions.
          </div>
        )}

        {data?.enabled && data.systems.map((system, i) => (
          <section key={i} className="mt-8" data-testid="zone-system">
            {multi && <h2 className="mb-3 text-lg font-bold text-white">{system.system_name}</h2>}
            {system.watering?.summary && (
              <p className="mb-3 rounded-xl border border-cyan-300/20 bg-cyan-400/10 px-4 py-2.5 text-sm font-medium text-cyan-50" data-testid="zone-watering">
                {system.watering.summary}
              </p>
            )}
            {system.zones.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-300">No zones listed yet.</div>
            ) : (
              <>
                {/* Desktop / tablet: table */}
                <div className="hidden overflow-hidden rounded-2xl border border-white/10 bg-white/5 md:block">
                  <table className="w-full text-left text-sm text-slate-100" data-testid="zone-table">
                    <thead className="bg-white/10 text-xs uppercase tracking-wider text-cyan-100">
                      <tr>
                        <th className="px-4 py-3 w-20">Zone</th>
                        <th className="px-4 py-3 w-24">Time</th>
                        <th className="px-4 py-3 w-40">Type</th>
                        <th className="px-4 py-3">Coverage Area</th>
                        <th className="px-4 py-3 w-20 text-right">GPM</th>
                        <th className="px-4 py-3 w-28 text-right">Gallons/Run</th>
                      </tr>
                    </thead>
                    <tbody>
                      {system.zones.map(z => (
                        <tr key={z.zone_number} className="border-t border-white/5">
                          <td className="px-4 py-3 font-semibold">{z.zone_number}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{fmtTime(z) || '—'}</td>
                          <td className="px-4 py-3">{z.zone_type || '—'}</td>
                          <td className="px-4 py-3">{z.coverage_area || '—'}</td>
                          <td className="px-4 py-3 text-right tabular-nums" data-testid="zone-gpm">{z.gpm || '—'}</td>
                          <td className="px-4 py-3 text-right tabular-nums" data-testid="zone-gallons">{fmtGal(z.gallons_per_run) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Phone: compact cards */}
                <div className="space-y-2 md:hidden" data-testid="zone-cards">
                  {system.zones.map(z => (
                    <div key={z.zone_number} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-400/15 text-sm font-bold text-cyan-100">
                        {z.zone_number}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-white">{z.coverage_area || `Zone ${z.zone_number}`}</div>
                        <div className="text-xs text-slate-300">
                          {[z.zone_type, fmtTime(z), fmtGpm(z), fmtGal(z.gallons_per_run)].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {system.water_use?.zones_running > 0 && (
                  <p className="mt-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-slate-100" data-testid="zone-total-gallons">
                    {system.water_use.complete
                      ? <>Total per complete cycle: <strong className="text-white">{system.water_use.total_display}</strong></>
                      : <>Total gallons per cycle: not available — GPM not recorded for {system.water_use.zones_missing_gpm} of {system.water_use.zones_running} zones</>}
                  </p>
                )}
              </>
            )}
          </section>
        ))}

        <p className="mt-10 text-center text-xs text-slate-400">Powered by Boatman Systems™</p>
      </div>
    </div>
  );
}
